import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Paper,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Alert,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { api } from '@/lib/api';
import { format } from 'date-fns';
import { dateFnsLocale } from '@/utils/locale';

interface DeliveryCounts {
  total: number;
  pending: number;
  sent: number;
  delivered: number;
  read: number;
  failed: number;
}

interface FailedItem {
  id: string;
  conversationId?: string;
  bookingId?: string;
  notificationType?: string;
  errorReason: string | null;
  customerName: string;
  customerPhone: string;
  createdAt: string;
}

interface DeliveryStatusResponse {
  messages: {
    counts: DeliveryCounts;
    recentFailures: FailedItem[];
  };
  notifications: {
    counts: DeliveryCounts;
    recentFailures: FailedItem[];
  };
}

function CountChip({ labelKey, count, color }: { labelKey: string; count: number; color: 'success' | 'warning' | 'error' | 'info' | 'default' }) {
  const { t } = useTranslation('messages');
  return (
    <Chip
      size="small"
      label={`${t(labelKey)}: ${String(count)}`}
      color={color}
      variant={count > 0 ? 'filled' : 'outlined'}
      sx={{ mr: 0.5, mb: 0.5 }}
    />
  );
}

function CountsSummary({ counts, label }: { counts: DeliveryCounts; label: string }) {
  const { t } = useTranslation('messages');
  const deliveryRate = counts.total > 0
    ? Math.round(((counts.delivered + counts.read) / counts.total) * 100)
    : 0;

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', mb: 1 }}>
        <CountChip labelKey="deliveryTotal" count={counts.total} color="default" />
        <CountChip labelKey="deliveryPending" count={counts.pending} color="info" />
        <CountChip labelKey="deliverySent" count={counts.sent} color="info" />
        <CountChip labelKey="deliveryDelivered" count={counts.delivered} color="success" />
        <CountChip labelKey="deliveryRead" count={counts.read} color="success" />
        <CountChip labelKey="deliveryFailed" count={counts.failed} color="error" />
      </Box>
      <Typography variant="body2" color="text.secondary">
        {t('deliveryRate')}: {String(deliveryRate)}%
      </Typography>
    </Paper>
  );
}

function FailureList({ failures, onNavigate }: { failures: FailedItem[]; onNavigate?: (conversationId: string) => void }) {
  const { t } = useTranslation('messages');

  if (failures.length === 0) {
    return (
      <Alert severity="success" icon={<CheckCircleOutlineIcon />} sx={{ mb: 2 }}>
        {t('noFailedDeliveries')}
      </Alert>
    );
  }

  return (
    <List dense disablePadding>
      {failures.map((item) => (
        <ListItem
          key={item.id}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            cursor: item.conversationId && onNavigate ? 'pointer' : 'default',
          }}
          onClick={() => {
            if (item.conversationId && onNavigate) {
              onNavigate(item.conversationId);
            }
          }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <ErrorOutlineIcon color="error" fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" fontWeight={500}>
                  {item.customerName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {item.customerPhone}
                </Typography>
              </Box>
            }
            secondary={
              <Box>
                <Typography variant="caption" color="error.main" sx={{ display: 'block' }}>
                  {item.errorReason ?? t('deliveryUnknownError')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                  <ScheduleIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
                  <Typography variant="caption" color="text.disabled">
                    {format(new Date(item.createdAt), 'd.M.yyyy HH:mm', { locale: dateFnsLocale() })}
                  </Typography>
                  {item.notificationType && (
                    <Chip size="small" label={item.notificationType} variant="outlined" sx={{ ml: 0.5, height: 18, fontSize: '0.65rem' }} />
                  )}
                </Box>
              </Box>
            }
          />
        </ListItem>
      ))}
    </List>
  );
}

interface DeliveryStatusPanelProps {
  onNavigateToConversation?: (conversationId: string) => void;
}

export function DeliveryStatusPanel({ onNavigateToConversation }: DeliveryStatusPanelProps) {
  const { t } = useTranslation('messages');

  const { data, isLoading, error } = useQuery({
    queryKey: ['delivery-status'],
    queryFn: () => api.get<DeliveryStatusResponse>('/conversations/delivery-status'),
    refetchInterval: 30_000,
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {t('deliveryStatusLoadFailed')}
      </Alert>
    );
  }

  const allFailures = [
    ...data.messages.recentFailures,
    ...data.notifications.recentFailures,
  ].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  return (
    <Box sx={{ p: 2, overflow: 'auto', height: '100%' }}>
      <Typography variant="h6" gutterBottom>
        {t('deliveryStatusTitle')}
      </Typography>

      <CountsSummary counts={data.messages.counts} label={t('deliveryMessages')} />
      <CountsSummary counts={data.notifications.counts} label={t('deliveryNotifications')} />

      <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
        {t('deliveryRecentFailures')}
      </Typography>
      <FailureList failures={allFailures} onNavigate={onNavigateToConversation} />
    </Box>
  );
}
